/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

// Port of vpx-js lib/vpt/surface/surface-mesh-generator.ts.
// Changes: input is a WallSpec; no texture coordinates (walls use plain materials);
// the outline always runs the way the ear clipper needs; a zero-length edge is safe.

import { ShapeUtils, Vector2 } from "three";
import { Vertex3DNoTex2 } from "../../engine/math/vertex.ts";
import { type RenderVertex, Vertex2D } from "../../engine/math/vertex2d.ts";
import { Mesh } from "../../engine/mesh.ts";
import type { WallSpec } from "../../shared/contracts.ts";
import { signedArea, smoothLoop } from "./shared.ts";

/** The meshes of one wall. The table frame is used (z is up). */
export interface WallMeshes {
  top?: Mesh;
  side?: Mesh;
}

/** The unit normal of each edge of a closed outline. Edge `i` runs from point `i` to the next. */
function edgeNormals(vVertex: readonly RenderVertex[]): Vertex2D[] {
  const count = vVertex.length;
  const normals: Vertex2D[] = [];
  for (let i = 0; i < count; i++) {
    const pv1 = vVertex[i] as RenderVertex;
    const pv2 = vVertex[(i + 1) % count] as RenderVertex;
    const dx = pv1.x - pv2.x;
    const dy = pv1.y - pv2.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const invLen = length > 0 ? 1.0 / length : 0.0;
    normals[i] = new Vertex2D(dy * invLen, dx * invLen);
  }
  return normals;
}

/** The normal at one end of an edge. A smooth corner takes the mean of the two edge normals. */
function endNormal(smooth: boolean, edge: Vertex2D, neighbour: Vertex2D): Vertex2D {
  const n = smooth
    ? new Vertex2D((neighbour.x + edge.x) * 0.5, (neighbour.y + edge.y) * 0.5)
    : new Vertex2D(edge.x, edge.y);
  n.normalize();
  return n;
}

/** Side: one quad per edge. */
function buildSide(
  sideMesh: Mesh,
  vVertex: readonly RenderVertex[],
  bottom: number,
  top: number,
): void {
  const count = vVertex.length;
  const rgNormal = edgeNormals(vVertex);
  let offset = 0;
  for (let i = 0; i < count; i++) {
    const pv1 = vVertex[i] as RenderVertex;
    const pv2 = vVertex[(i + 1) % count] as RenderVertex;
    const nI = rgNormal[i] as Vertex2D;
    const n0 = endNormal(pv1.fSmooth, nI, rgNormal[(i + count - 1) % count] as Vertex2D);
    const n1 = endNormal(pv2.fSmooth, nI, rgNormal[(i + 1) % count] as Vertex2D);

    const corners: Array<[number, number, number, Vertex2D]> = [
      [pv1.x, pv1.y, bottom, n0],
      [pv1.x, pv1.y, top, n0],
      [pv2.x, pv2.y, top, n1],
      [pv2.x, pv2.y, bottom, n1],
    ];
    for (const [x, y, z, n] of corners) {
      const v = new Vertex3DNoTex2();
      v.x = x;
      v.y = y;
      v.z = z;
      v.nx = n.x;
      v.ny = -n.y;
      v.nz = 0;
      sideMesh.vertices.push(v);
    }
    sideMesh.indices.push(offset, offset + 1, offset + 2, offset, offset + 2, offset + 3);
    offset += 4;
  }
}

/** Top: ear clipping of the outline. Returns false when no triangle comes out. */
function buildTop(topMesh: Mesh, vVertex: readonly RenderVertex[], top: number): boolean {
  const count = vVertex.length;
  const vPoly: number[] = [];
  for (let i = 0; i < count; i++) vPoly[i] = i;
  topMesh.indices = Mesh.polygonToTriangles(vVertex as RenderVertex[], vPoly);
  if (topMesh.indices.length < (count - 2) * 3) {
    // The ear clipper gave up (a very thin or touching outline). Use the library triangulation.
    const contour = vVertex.map((v) => new Vector2(v.x, v.y));
    topMesh.indices = ShapeUtils.triangulateShape(contour, []).flat();
  }
  if (topMesh.indices.length === 0) return false;

  for (let i = 0; i < count; i++) {
    const pv0 = vVertex[i] as RenderVertex;
    const v = new Vertex3DNoTex2();
    v.x = pv0.x;
    v.y = pv0.y;
    v.z = top;
    v.nx = 0;
    v.ny = 0;
    v.nz = 1.0;
    topMesh.vertices.push(v);
  }
  return true;
}

/**
 * Build the top and the side mesh of a wall.
 * @param spec the wall
 * @param tableHeight base height of the playfield
 */
export function generateWallMeshes(spec: WallSpec, tableHeight = 0): WallMeshes {
  const topMesh = new Mesh(`surface.top-${spec.name}`);
  const sideMesh = new Mesh(`surface.side-${spec.name}`);

  const vVertex: RenderVertex[] = smoothLoop(spec.dragPoints);
  // The ear clipper and the side normals need an outline that runs counter-clockwise on the
  // screen (y down). The contract lists outlines clockwise, so this code turns them around.
  if (signedArea(vVertex) > 0) vVertex.reverse();

  const numVertices = vVertex.length;
  if (numVertices < 3) return {};

  const bottom = (spec.heightBottom ?? 0) + tableHeight;
  const top = spec.heightTop + tableHeight;

  buildSide(sideMesh, vVertex, bottom, top);
  if (!buildTop(topMesh, vVertex, top)) return {};

  const meshes: WallMeshes = { top: topMesh };
  if (top !== bottom) meshes.side = sideMesh;
  return meshes;
}
